import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { PLANS, CURRENCY } from '@/lib/pricing';
import { useSubscription } from '@/lib/hooks/useSubscription';
import { CurrentPlanBanner } from '@/components/CurrentPlanBanner';
import { UpgradeConfirmationModal } from '@/components/UpgradeConfirmationModal';

interface StripePrice {
  id: string;
  productId: string;
  unitAmount: number;
  currency: string;
  interval: string;
}

export default function SelectPlanPage() {
  const { subscription, team } = useSubscription();
  const hasActiveSubscription = !!team?.stripeSubscriptionId;
  const isCurrentlyTrialing = subscription?.isTrialing ?? false;

  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    plan: 'starter' | 'pro';
    priceId: string;
    planName: string;
  } | null>(null);

  useEffect(() => {
    // Fetch Stripe prices
    fetch('/api/stripe/prices')
      .then((res) => res.json())
      .then((data) => setPrices(data))
      .catch(() => setError('Failed to load pricing'));
  }, []);

  const formatPrice = (priceInPence: number) => {
    return `${CURRENCY.symbol}${(priceInPence / 100).toFixed(0)}`;
  };

  const handleSelectPlan = async (planKey: 'starter' | 'pro') => {
    setSelectedPlan(planKey);
    setError('');

    const plan = PLANS[planKey];
    const interval = billingInterval === 'monthly' ? 'month' : 'year';

    // Find the matching Stripe price
    const matchingPrice = prices.find(
      (p) =>
        p.interval === interval &&
        p.unitAmount === (billingInterval === 'monthly' ? plan.pricing.monthly.price : plan.pricing.annual.price)
    );

    if (!matchingPrice) {
      setError('Could not find matching price. Please try again.');
      setSelectedPlan(null);
      return;
    }

    // Check if user already has subscription - show modal
    if (hasActiveSubscription) {
      setPendingUpgrade({
        plan: planKey,
        priceId: matchingPrice.id,
        planName: plan.name,
      });
      setShowUpgradeModal(true);
      return;
    }

    // New user flow - proceed to checkout
    setLoading(true);

    try {
      const response = await fetch('/api/stripe/signup-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: matchingPrice.id,
          planName: plan.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!pendingUpgrade) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/stripe/change-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: pendingUpgrade.priceId,
          planName: pendingUpgrade.planName,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to upgrade plan');
      }

      // Redirect to campaigns with success message
      window.location.href = '/dashboard/campaigns?upgrade=success';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upgrade failed');
      setShowUpgradeModal(false);
    } finally {
      setLoading(false);
      setPendingUpgrade(null);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-semibold text-content-heading">
            Choose your plan
          </h1>
          <p className="mt-3 text-content-secondary">
            {hasActiveSubscription
              ? 'Select a new plan to upgrade your subscription'
              : 'Start your 14-day free trial. No charge until your trial ends.'}
          </p>
        </div>

        {isCurrentlyTrialing && subscription && (
          <CurrentPlanBanner
            planName={subscription.planName ?? 'trial'}
            trialDaysRemaining={subscription.trialDaysRemaining ?? 0}
            trialEndsAt={subscription.trialEndsAt ?? new Date()}
          />
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-surface-track rounded-lg p-1 flex">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingInterval === 'monthly'
                  ? 'bg-surface-raised text-content-heading shadow-sm'
                  : 'text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingInterval === 'annual'
                  ? 'bg-surface-raised text-content-heading shadow-sm'
                  : 'text-foreground'
              }`}
            >
              Annual
              <span className="ml-1 text-success-foreground text-xs">Save 20%</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-danger-surface border border-danger-border rounded-md p-4 text-center">
            <p className="text-danger-heading text-sm">{error}</p>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Starter Plan */}
          <Card className={`p-6 relative flex flex-col ${selectedPlan === 'starter' ? 'ring-2 ring-primary' : ''}`}>
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-content-heading">
                {PLANS.starter.name}
              </h3>
              <p className="text-content-secondary text-sm mt-1">
                {PLANS.starter.description}
              </p>
            </div>

            <div className="mb-6">
              <span className="text-4xl font-medium text-content-heading">
                {formatPrice(PLANS.starter.pricing[billingInterval].price)}
              </span>
              <span className="text-content-secondary">
                /{billingInterval === 'monthly' ? 'month' : 'year'}
              </span>
              {billingInterval === 'annual' && (
                <p className="text-sm text-success-foreground mt-1">
                  Save {formatPrice(PLANS.starter.pricing.annual.savings)}/year
                </p>
              )}
            </div>

            <ul className="space-y-3 mb-6 grow">
              {PLANS.starter.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-content-secondary">
                  <CheckIcon className="h-4 w-4 text-success-solid mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              onClick={() => handleSelectPlan('starter')}
              disabled={loading}
              className="w-full mt-auto"
              variant="secondary"
            >
              {loading && selectedPlan === 'starter' ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Redirecting...
                </>
              ) : hasActiveSubscription ? (
                'Upgrade to Starter'
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </Card>

          {/* Pro Plan */}
          <Card className={`p-6 relative border-primary flex flex-col ${selectedPlan === 'pro' ? 'ring-2 ring-primary' : ''}`}>
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-content-heading">
                {PLANS.pro.name}
              </h3>
              <p className="text-content-secondary text-sm mt-1">
                {PLANS.pro.description}
              </p>
            </div>

            <div className="mb-6">
              <span className="text-4xl font-medium text-content-heading">
                {formatPrice(PLANS.pro.pricing[billingInterval].price)}
              </span>
              <span className="text-content-secondary">
                /{billingInterval === 'monthly' ? 'month' : 'year'}
              </span>
              {billingInterval === 'annual' && (
                <p className="text-sm text-success-foreground mt-1">
                  Save {formatPrice(PLANS.pro.pricing.annual.savings)}/year
                </p>
              )}
            </div>

            <ul className="space-y-3 mb-6 grow">
              {PLANS.pro.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-content-secondary">
                  <CheckIcon className="h-4 w-4 text-success-solid mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              onClick={() => handleSelectPlan('pro')}
              disabled={loading}
              className="w-full mt-auto"
              variant="primary"
            >
              {loading && selectedPlan === 'pro' ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Redirecting...
                </>
              ) : hasActiveSubscription ? (
                'Upgrade to Pro'
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          {hasActiveSubscription ? (
            <>
              Upgrading will end your trial immediately and billing will start today.
            </>
          ) : (
            <>
              Your card will be charged after your 14-day trial ends.
              <br />
              Cancel anytime during the trial at no cost.
            </>
          )}
        </p>

        <UpgradeConfirmationModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setPendingUpgrade(null);
            setSelectedPlan(null);
          }}
          onConfirm={handleConfirmUpgrade}
          currentPlan={subscription?.planName ?? ''}
          targetPlan={pendingUpgrade?.planName ?? ''}
          trialDaysRemaining={subscription?.trialDaysRemaining ?? 0}
          loading={loading}
        />
      </div>
    </div>
  );
}
